/**
 * Matrix Pagination System
 * 
 * Handles large configuration matrices by splitting them into manageable pages.
 * Provides navigation controls and maintains selection state across pages.
 */

import { createMatrixComponent } from './matrix.js';

/**
 * Create paginated matrix wrapper
 * @param {Object} matrixData - Full matrix data from transformToMatrix()
 * @param {Object} formData - Form data for units and configuration
 * @param {HTMLElement} container - Container element to render into
 * @param {Object} options - Pagination options
 * @returns {Object} Paginated matrix component
 */
export function createPaginatedMatrix(matrixData, formData, container, options = {}) {
  const {
    pageSize = 5,           // Size of each page (5×5 matrix per page)
    showPagination = true,  // Show pagination controls
    showStats = true        // Show pagination statistics
  } = options;
  
  const { dimensions } = matrixData;
  // Always render pagination controls so labels are visible
  
  // Calculate pagination dimensions
  const xPages = Math.ceil(dimensions.width / pageSize);
  const yPages = Math.ceil(dimensions.height / pageSize);
  const totalPages = xPages * yPages;
  
  // State
  let currentXPage = 0;
  let currentYPage = 0;
  let currentMatrixComponent = null;
  let onCellSelectCallback = null;
  let onCellHoverCallback = null;
  let onHoverEndCallback = null;
  
  // Clear container
  container.innerHTML = '';
  
  // Create wrapper
  const wrapper = document.createElement('div');
  wrapper.className = 'paginated-matrix-wrapper w-full';
  
  // Create separated navigation controls
  let horizontalNav = null;
  let verticalNav = null;
  if (showPagination) {
    horizontalNav = createHorizontalNavigation(
      xPages, currentXPage,
      (newXPage) => navigateToPage(newXPage, currentYPage)
    );
    verticalNav = createVerticalNavigation(
      yPages, currentYPage,
      (newYPage) => navigateToPage(currentXPage, newYPage)
    );
    wrapper.appendChild(horizontalNav);
  }
  
  // Create matrix container with vertical nav
  const matrixContainer = document.createElement('div');
  matrixContainer.className = 'paginated-matrix-container w-full overflow-x-auto';
  // Styling handled in CSS (.paginated-matrix-container)
  
  if (showPagination && verticalNav) {
    // Create container with matrix + vertical nav
    const matrixWithNav = document.createElement('div');
    matrixWithNav.className = 'matrix-with-vertical-nav';
    // Positioning handled in CSS (.matrix-with-vertical-nav)
    matrixWithNav.appendChild(matrixContainer);
    matrixWithNav.appendChild(verticalNav);
    // Add right padding so nav doesn't overlap matrix content
    matrixContainer.classList.add('pr-14');
    wrapper.appendChild(matrixWithNav);
  } else {
    wrapper.appendChild(matrixContainer);
  }
  
  // Create statistics panel
  let statsPanel = null;
  if (showStats) {
    statsPanel = createStatsPanel(matrixData, currentXPage, currentYPage, xPages, yPages, pageSize);
    wrapper.appendChild(statsPanel);
  }
  
  container.appendChild(wrapper);
  
  // Render initial page
  renderCurrentPage();
  
  /**
   * Navigate to specific page
   * @param {number} xPage - X page index (0-based)
   * @param {number} yPage - Y page index (0-based)
   */
  function navigateToPage(xPage, yPage) {
    if (xPage >= 0 && xPage < xPages && yPage >= 0 && yPage < yPages) {
      currentXPage = xPage;
      currentYPage = yPage;
      renderCurrentPage();
      
      if (horizontalNav) {
        updateHorizontalNavigation(horizontalNav, xPages, currentXPage);
      }
      if (verticalNav) {
        updateVerticalNavigation(verticalNav, yPages, currentYPage);
      }
      
      if (statsPanel) {
        updateStatsPanel(statsPanel, matrixData, currentXPage, currentYPage, xPages, yPages, pageSize);
      }
    }
  }
  
  /**
   * Render current page matrix
   */
  function renderCurrentPage() {
    const pageMatrixData = extractPageData(matrixData, currentXPage, currentYPage, pageSize);
    
    // Create matrix component
    currentMatrixComponent = createMatrixComponent(pageMatrixData, formData, matrixContainer);
    
    // Forward cell selection events with adjusted coordinates; preserve nulls for deselection
    if (onCellSelectCallback) {
      currentMatrixComponent.onCellSelect((localXIndex, localYIndex, cellElement) => {
        if (localXIndex == null || localYIndex == null) {
          onCellSelectCallback(null, null, null);
          return;
        }
        const globalXIndex = currentXPage * pageSize + localXIndex;
        const globalYIndex = currentYPage * pageSize + localYIndex;
        onCellSelectCallback(globalXIndex, globalYIndex, cellElement);
      });
    }

    // Forward hover events with adjusted coordinates
    if (onCellHoverCallback) {
      currentMatrixComponent.onCellHover((localXIndex, localYIndex, cellElement) => {
        const globalXIndex = currentXPage * pageSize + localXIndex;
        const globalYIndex = currentYPage * pageSize + localYIndex;
        onCellHoverCallback(globalXIndex, globalYIndex, cellElement);
      });
    }
    if (onHoverEndCallback) {
      currentMatrixComponent.onHoverEnd(() => {
        onHoverEndCallback();
      });
    }
  }
  
  // Component interface
  return {
    element: wrapper,
    selectCell: (globalXIndex, globalYIndex) => {
      // Calculate which page contains this cell
      const targetXPage = Math.floor(globalXIndex / pageSize);
      const targetYPage = Math.floor(globalYIndex / pageSize);
      
      if (targetXPage !== currentXPage || targetYPage !== currentYPage) {
        navigateToPage(targetXPage, targetYPage);
      }
      
      // Calculate local coordinates and select
      const localXIndex = globalXIndex % pageSize;
      const localYIndex = globalYIndex % pageSize;
      
      if (currentMatrixComponent) {
        currentMatrixComponent.selectCell(localXIndex, localYIndex);
      }
    },
    getSelectedCell: () => {
      if (currentMatrixComponent) {
        const localSelection = currentMatrixComponent.getSelectedCell();
        if (localSelection) {
          return {
            x: currentXPage * pageSize + localSelection.x,
            y: currentYPage * pageSize + localSelection.y
          };
        }
      }
      return null;
    },
    onCellSelect: (callback) => {
      onCellSelectCallback = callback;
      if (currentMatrixComponent) {
        currentMatrixComponent.onCellSelect((localXIndex, localYIndex, cellElement) => {
          if (localXIndex == null || localYIndex == null) {
            callback(null, null, null);
            return;
          }
          const globalXIndex = currentXPage * pageSize + localXIndex;
          const globalYIndex = currentYPage * pageSize + localYIndex;
          callback(globalXIndex, globalYIndex, cellElement);
        });
      }
    },
    onCellHover: (callback) => {
      onCellHoverCallback = callback;
      if (currentMatrixComponent) {
        currentMatrixComponent.onCellHover((localXIndex, localYIndex, cellElement) => {
          const globalXIndex = currentXPage * pageSize + localXIndex;
          const globalYIndex = currentYPage * pageSize + localYIndex;
          callback(globalXIndex, globalYIndex, cellElement);
        });
      }
    },
    onHoverEnd: (callback) => {
      onHoverEndCallback = callback;
      if (currentMatrixComponent) {
        currentMatrixComponent.onHoverEnd(() => callback());
      }
    },
    getCurrentPage: () => ({ x: currentXPage, y: currentYPage }),
    getTotalPages: () => ({ x: xPages, y: yPages }),
    navigateToPage,
    refresh: (newMatrixData, newFormData) => {
      return createPaginatedMatrix(newMatrixData, newFormData, container, options);
    }
  };
}

/**
 * Extract data for a specific page
 * @param {Object} fullMatrixData - Complete matrix data
 * @param {number} xPage - X page index
 * @param {number} yPage - Y page index  
 * @param {number} pageSize - Size of each page
 * @returns {Object} Matrix data for this page
 */
function extractPageData(fullMatrixData, xPage, yPage, pageSize) {
  const { matrix, xAxis, yAxis } = fullMatrixData;
  
  // Calculate bounds for this page
  const xStart = xPage * pageSize;
  const xEnd = Math.min(xStart + pageSize, xAxis.length);
  const yStart = yPage * pageSize;
  const yEnd = Math.min(yStart + pageSize, yAxis.length);
  
  // Extract sub-matrix
  const pageMatrix = [];
  for (let y = yStart; y < yEnd; y++) {
    const pageRow = [];
    for (let x = xStart; x < xEnd; x++) {
      pageRow.push(matrix[y][x]);
    }
    pageMatrix.push(pageRow);
  }
  
  // Extract axis data for this page
  const pageXAxis = xAxis.slice(xStart, xEnd).map((item, index) => ({
    ...item,
    index // Keep local index for page
  }));
  
  const pageYAxis = yAxis.slice(yStart, yEnd).map((item, index) => ({
    ...item,
    index // Keep local index for page
  }));
  
  // Count total configurations in this page
  let pageConfigCount = 0;
  pageMatrix.forEach(row => {
    row.forEach(cell => {
      if (cell && cell.count) {
        pageConfigCount += cell.count;
      }
    });
  });
  
  return {
    matrix: pageMatrix,
    xAxis: pageXAxis,
    yAxis: pageYAxis,
    dimensions: {
      width: pageXAxis.length,
      height: pageYAxis.length
    },
    totalConfigurations: pageConfigCount,
    getCellContent: (x, y) => {
      if (y >= 0 && y < pageMatrix.length && x >= 0 && x < pageMatrix[y].length) {
        return pageMatrix[y][x];
      }
      return null;
    },
    getConfigurationsByMargins: (hMargin, vMargin) => {
      // This would need the original data, but for page display we don't need it
      return [];
    },
    getAxisLabels: () => ({ x: pageXAxis, y: pageYAxis })
  };
}

/**
 * Create horizontal navigation controls (top)
 * @param {number} xPages - Total X pages
 * @param {number} currentXPage - Current X page
 * @param {Function} onNavigate - Navigation callback
 * @returns {HTMLElement} Horizontal navigation element
 */
function createHorizontalNavigation(xPages, currentXPage, onNavigate) {
  const nav = document.createElement('div');
  nav.className = 'horizontal-navigation flex justify-start items-center gap-3 mb-4 py-2';
  // Layout handled in CSS (.horizontal-navigation)
  
  // Left arrow
  const leftBtn = document.createElement('button');
  leftBtn.className = 'nav-arrow nav-left w-8 h-8 border border-gray-300 rounded-md bg-white flex items-center justify-center cursor-pointer transition text-xs text-gray-700 shadow-sm enabled:hover:border-gray-400 enabled:hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400';
  leftBtn.innerHTML = '◀';
  leftBtn.title = 'Previous horizontal page';
  // Hover/disabled states handled in CSS
  leftBtn.onclick = () => onNavigate(Math.max(0, currentXPage - 1));
  
  // Page indicator
  const pageIndicator = document.createElement('div');
  pageIndicator.className = 'page-indicator text-[13px] text-gray-700 font-medium text-center px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md font-mono min-w-[70px]';
  // Styling handled in CSS (.page-indicator)
  
  // Right arrow  
  const rightBtn = document.createElement('button');
  rightBtn.className = 'nav-arrow nav-right w-8 h-8 border border-gray-300 rounded-md bg-white flex items-center justify-center cursor-pointer transition text-xs text-gray-700 shadow-sm enabled:hover:border-gray-400 enabled:hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400';
  rightBtn.innerHTML = '▶';
  rightBtn.title = 'Next horizontal page';
  // Hover/disabled states handled in CSS
  rightBtn.onclick = () => onNavigate(Math.min(xPages - 1, currentXPage + 1));
  
  nav.appendChild(leftBtn);
  nav.appendChild(pageIndicator);
  nav.appendChild(rightBtn);
  
  updateHorizontalNavigation(nav, xPages, currentXPage);
  
  return nav;
}

/**
 * Create vertical navigation controls (right side)
 * @param {number} yPages - Total Y pages
 * @param {number} currentYPage - Current Y page
 * @param {Function} onNavigate - Navigation callback
 * @returns {HTMLElement} Vertical navigation element
 */
function createVerticalNavigation(yPages, currentYPage, onNavigate) {
  const nav = document.createElement('div');
  nav.className = 'vertical-navigation flex flex-col items-center gap-3 p-0 justify-center z-20 absolute right-2 top-0';
  // Layout handled in CSS (.vertical-navigation)
  
  // Up arrow
  const upBtn = document.createElement('button');
  upBtn.className = 'nav-arrow nav-up w-8 h-8 border border-gray-300 rounded-md bg-white flex items-center justify-center cursor-pointer transition text-xs text-gray-700 shadow-sm enabled:hover:border-gray-400 enabled:hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400';
  upBtn.innerHTML = '▲';
  upBtn.title = 'Previous vertical page';
  // Hover/disabled states handled in CSS
  upBtn.onclick = () => onNavigate(Math.max(0, currentYPage - 1));
  
  // Page indicator
  const pageIndicator = document.createElement('div');
  pageIndicator.className = 'page-indicator text-[13px] text-gray-700 font-medium text-center bg-gray-50 border border-gray-200 rounded-md font-mono min-h-[60px] flex items-center justify-center py-3 px-1.5 [writing-mode:vertical-rl] [text-orientation:mixed]';
  // Styling handled in CSS (.vertical-navigation .page-indicator)
  
  // Down arrow
  const downBtn = document.createElement('button');
  downBtn.className = 'nav-arrow nav-down w-8 h-8 border border-gray-300 rounded-md bg-white flex items-center justify-center cursor-pointer transition text-xs text-gray-700 shadow-sm enabled:hover:border-gray-400 enabled:hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400';
  downBtn.innerHTML = '▼';
  downBtn.title = 'Next vertical page';
  // Hover/disabled states handled in CSS
  downBtn.onclick = () => onNavigate(Math.min(yPages - 1, currentYPage + 1));
  
  nav.appendChild(upBtn);
  nav.appendChild(pageIndicator);
  nav.appendChild(downBtn);
  
  updateVerticalNavigation(nav, yPages, currentYPage);
  
  return nav;
}

/**
 * Update horizontal navigation state
 * @param {HTMLElement} nav - Horizontal navigation element
 * @param {number} xPages - Total X pages
 * @param {number} currentXPage - Current X page
 */
function updateHorizontalNavigation(nav, xPages, currentXPage) {
  const leftBtn = nav.querySelector('.nav-left');
  const rightBtn = nav.querySelector('.nav-right');
  const pageIndicator = nav.querySelector('.page-indicator');
  
  // Update button states
  leftBtn.disabled = (currentXPage === 0);
  rightBtn.disabled = (currentXPage === xPages - 1);
  
  // Update page indicator
  pageIndicator.textContent = `H-margins ${currentXPage + 1} of ${xPages}`;
  
  // Visual states handled purely via CSS (:disabled)
}

/**
 * Update vertical navigation state
 * @param {HTMLElement} nav - Vertical navigation element
 * @param {number} yPages - Total Y pages
 * @param {number} currentYPage - Current Y page
 */
function updateVerticalNavigation(nav, yPages, currentYPage) {
  const upBtn = nav.querySelector('.nav-up');
  const downBtn = nav.querySelector('.nav-down');
  const pageIndicator = nav.querySelector('.page-indicator');
  
  // Update button states
  upBtn.disabled = (currentYPage === 0);
  downBtn.disabled = (currentYPage === yPages - 1);
  
  // Update page indicator
  pageIndicator.textContent = `V-margins ${currentYPage + 1} of ${yPages}`;
  
  // Visual states handled purely via CSS (:disabled)
}

/**
 * Create statistics panel
 * @param {Object} matrixData - Full matrix data
 * @param {number} currentXPage - Current X page
 * @param {number} currentYPage - Current Y page
 * @param {number} xPages - Total X pages
 * @param {number} yPages - Total Y pages
 * @param {number} pageSize - Page size
 * @returns {HTMLElement} Statistics panel element
 */
function createStatsPanel(matrixData, currentXPage, currentYPage, xPages, yPages, pageSize) {
  const panel = document.createElement('div');
  panel.className = 'pagination-stats';
  // Styling handled in CSS (.pagination-stats)
  
  updateStatsPanel(panel, matrixData, currentXPage, currentYPage, xPages, yPages, pageSize);
  
  return panel;
}

/**
 * Update statistics panel content
 * @param {HTMLElement} panel - Stats panel element
 * @param {Object} matrixData - Full matrix data
 * @param {number} currentXPage - Current X page
 * @param {number} currentYPage - Current Y page
 * @param {number} xPages - Total X pages
 * @param {number} yPages - Total Y pages
 * @param {number} pageSize - Page size
 */
function updateStatsPanel(panel, matrixData, currentXPage, currentYPage, xPages, yPages, pageSize) {
  const { dimensions, totalConfigurations } = matrixData;
  
  // Calculate current page bounds
  const xStart = currentXPage * pageSize + 1;
  const xEnd = Math.min((currentXPage + 1) * pageSize, dimensions.width);
  const yStart = currentYPage * pageSize + 1;  
  const yEnd = Math.min((currentYPage + 1) * pageSize, dimensions.height);
  
  panel.innerHTML = `
    <div class="pagination-left">
      <span>Showing H-margins ${xStart}-${xEnd} of ${dimensions.width}, V-margins ${yStart}-${yEnd} of ${dimensions.height}</span>
    </div>
    <div class="pagination-right">
      <span>${totalConfigurations} total configurations • ${xPages}×${yPages} pages</span>
    </div>
  `;
}
